import { ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import {
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MatLegacyAutocompleteSelectedEvent as MatAutocompleteSelectedEvent } from '@angular/material/legacy-autocomplete';
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { Observable, Subject, throwError } from 'rxjs';
import {
  takeUntil,
  catchError,
  tap,
  debounceTime,
  distinctUntilChanged,
} from 'rxjs/operators';

import {
  IClientCreditMemo,
  IClientCreditMemoInvoiceEntry,
  ICompany,
} from '../../../client/interfaces';
import { IInvoice } from '../../../invoice/interfaces/invoice.interface';
import { SnackBarConfig } from '../../../shared/configs/snackbar.config';

// SERVICES
import { ClientCreditMemoApiService } from '../../../shared/services/api/client/client-credit-memo-api.service';
import { ClientPurchaseOrderApiService } from '../../../shared/services/api/client/client-purchase-order-api.service';
import { InvoiceApiService } from '../../../shared/services/api/invoice-api.service';

//INTERFACES
import { trackById } from '../../../shared/util';

interface DataInterface {
  activeCompany :ICompany;
  creditMemo :IClientCreditMemo;
}
@Component({
  selector: 'lum-credit-memo-form',
  templateUrl: './credit-memo-form.component.html',
  styleUrls: ['./credit-memo-form.component.scss'],
})
export class CreditMemoFormComponent implements OnInit {
  form :UntypedFormGroup;
  invoiceLoading$ :Observable<boolean>;
  invoiceEntries :{
    invoiceId ?:number;
    amount :number;
  }[] = [];
  invoices :IInvoice[] = [];
  invoiceInput$ = new Subject<string>();
  invoices$ :Observable<IInvoice[]>;
  loading :boolean = false;
  selectedInvoices :number[] = [];
  filteredInvoices :IInvoice[] = [];

  trackById = trackById;

  private onDestroy$ = new Subject();

  constructor(
    @Inject(MAT_DIALOG_DATA) private data :DataInterface,
    private dialogRef :MatDialogRef<CreditMemoFormComponent>,
    private fb :UntypedFormBuilder,
    private clientCreditMemoApiService :ClientCreditMemoApiService,
    private snackBar :MatSnackBar,
    private clientPurchaseOrderApiService :ClientPurchaseOrderApiService,
    private cd :ChangeDetectorRef,
  ) {}

  ngOnInit() :void {
    this.getDataFromService();
    this.initFormData();
    this.getInvoice().subscribe();
    this.onInputInvoice().subscribe();
  }

  onSubmit() :void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.snackBar.open('Please fill the required fields.', 'Error', {
        duration: SnackBarConfig.DEFAULT.duration,
      });

      return;
    }
    if (this.hasGreaterTotalInvoiceEntryAmount) {
      this.snackBar.open(
        'Total amount of Invoice Entry is greater than the Credit Memo Value.',
        'Fail',
        {
          duration: 5000, //5 seconds
        },
      );

      return;
    }
    const payload = {
      ...this.form.value,
    };
    payload.invoiceEntries = payload.invoiceEntries.map(
      (invoiceEntry :IClientCreditMemoInvoiceEntry) => {
        return {
          invoiceId: invoiceEntry.invoiceId,
          amount: invoiceEntry.amount,
        };
      },
    );

    //added loading because observable loading won't work
    this.loading = true;
    if (this.data.creditMemo?.id) {
      this.clientCreditMemoApiService
        .update(this.data.activeCompany.id, this.data.creditMemo.id, payload)
        .pipe(
          catchError((response) => {
            this.loading = false;
            this.cd.detectChanges();
            this.snackBar.open(response.error, 'Fail', {
              duration: SnackBarConfig.DEFAULT.duration,
            });

            return throwError(() => response.error);
          }),
          takeUntil(this.onDestroy$),
        )
        .subscribe({
          next: (response) => {
            this.dialogRef.close(response);
          },
        });
    } else {
      this.clientCreditMemoApiService
        .create(this.data.activeCompany.id, payload)
        .pipe(
          catchError((response) => {
            this.loading = false;
            this.cd.detectChanges();
            this.snackBar.open(response.error, 'Fail', {
              duration: SnackBarConfig.DEFAULT.duration,
            });

            return throwError(() => response.error);
          }),
          takeUntil(this.onDestroy$),
        )
        .subscribe({
          next: (response) => {
            this.dialogRef.close(response);
          },
        });
    }
  }

  addInvoiceEntry() :void {
    if (this.hasGreaterCreditMemoAmount) {
      this.snackBar.open(
        'Can not add Invoice Entry if Credit Memo Amount is less than or equal the total Invoice Entries Amount.',
        'Fail',
        {
          duration: 5000, //5 seconds
        },
      );

      return;
    }

    this.invoiceEntriesControl.push(
      this.fb.group({
        invoiceId: [null, Validators.required],
        amount: [0, [Validators.required, Validators.min(0.1)]],
        isSelected: [false, Validators.requiredTrue],
      }),
    );
  }

  displayFn(invoiceEntry :any) :string {
    return invoiceEntry && invoiceEntry.invoiceId ? invoiceEntry.invoiceId : '';
  }

  onInvoiceInputChange(event :any, index :number) :void {
    this.invoiceInput$.next(event?.target.value);
    if (this.invoiceEntriesControl.controls[index].value.isSelected) {
      this.invoiceEntriesControl.controls[index].patchValue({
        isSelected: false,
      });
    }
  }

  onRemoveInvoiceEntry(index :number) :void {
    this.invoiceEntriesControl.removeAt(index);
  }

  onSelectedInvoice(event :MatAutocompleteSelectedEvent, index :number) :void {
    this.getFilteredInvoice();
    this.invoiceEntriesControl.controls[index].patchValue({ isSelected: true });
  }

  private initFormData() :void {
    this.form = this.fb.group({
      label: [this.data?.creditMemo?.label || null, Validators.required],
      creditMemo: [
        this.data?.creditMemo?.creditMemo || 0,
        [Validators.required, Validators.min(0.1)],
      ],
      remarks: [this.data?.creditMemo?.remarks || null],
      invoiceEntries: this.fb.array([]),
    });
    this.clientPurchaseOrderApiService.get({
      client_id: this.data.activeCompany.id,
      excludeFullyPaidInvoice: true,
    });
  }

  private getDataFromService() :void {
    this.invoices$ = this.clientPurchaseOrderApiService.purchaseOrders$;
    this.invoiceLoading$ = this.clientPurchaseOrderApiService.loading$;
  }

  // TODO: MOVE TO PO SERVICE
  private getInvoice() :Observable<any> {
    return this.invoices$.pipe(
      tap((invoices) => {
        this.invoices = invoices;
        this.getFilteredInvoice();
      }),
      takeUntil(this.onDestroy$),
    );
  }

  private getFilteredInvoice() :void {
    const invoiceEntryControl :number[] = this.invoiceEntriesControl.controls.map(
      (invoiceEntry) => invoiceEntry.value.invoiceId,
    );
    this.filteredInvoices = this.invoices.filter(
      (invoice) => !invoiceEntryControl?.includes(invoice.id),
    );
  }

  private onInputInvoice() :Observable<string> {
    return this.invoiceInput$.asObservable().pipe(
      debounceTime(500), // 0.5 sec delay
      distinctUntilChanged(),
      tap((searchValue :string) => {
        this.clientPurchaseOrderApiService.get({
          id: searchValue,
          client_id: this.data.activeCompany.id,
          excludeFullyPaidInvoice: true,
        });
      }),
      takeUntil(this.onDestroy$),
    );
  }

  get invoiceEntriesControl() :UntypedFormArray {
    return this.form.controls['invoiceEntries'] as UntypedFormArray;
  }

  get mode() :string {
    return this.data.creditMemo ? 'Update' : 'Create';
  }

  get totalInvoiceEntryAmount() :number {
    return this.invoiceEntriesControl.value
      .map((value :{ amount :number }) => value.amount)
      .reduce((acc :number, curr :number) => acc + curr, 0);
  }

  get labelControl() :UntypedFormControl {
    return this.form.get('label') as UntypedFormControl;
  }

  get creditMemoControl() :UntypedFormControl {
    return this.form.get('creditMemo') as UntypedFormControl;
  }

  get hasGreaterCreditMemoAmount() :boolean {
    return this.totalInvoiceEntryAmount >= this.creditMemoControl.value;
  }

  get hasGreaterTotalInvoiceEntryAmount() :boolean {
    return this.totalInvoiceEntryAmount > this.creditMemoControl.value;
  }
}
